import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useGraph, useSimulationStore, useRunConfig } from '../../../store/simulationStore';
import { useScenarioStore, useScenarios, useActiveScenario } from '../../../store/scenarioStore';
import { useScenarioValidation } from '../hooks/useScenarioValidation';
import { useSimulationRunner } from '../../../features/simulation/useSimulationRunner';
import type { SimulationNode, Scenario } from '../../../models/simulation';
import { NodeCompareSelector } from './NodeCompareSelector';
import { deriveFormulaUnit } from '../../../utils/formulaUnits';
import { ScenariosTable } from './ScenariosTable';
import { ScenarioInputDistributionsPanel } from './ScenarioInputDistributionsPanel';
import { ScenarioTab } from './ScenarioTab';
import { useResize } from '../../../hooks/useResize';
import { confirmationService } from '../../../services/confirmationService';
import { useVersionStore } from '../../../store/versionStore';
import { errorService } from '../../../services/errorService';

// Derive unit for nodes
const getNodeUnit = (node: SimulationNode, allNodes: SimulationNode[]): string | undefined => {
  if (node.data.type === 'distribution') {
    return node.data.unit;
  }
  if (node.data.type === 'formula') {
    return deriveFormulaUnit(node, allNodes);
  }
  return undefined;
};

export const ScenariosPanel = () => {
  const graph = useGraph();
  const runConfig = useRunConfig();
  const { run: runSimulation } = useSimulationRunner();
  const setSelection = useSimulationStore((s) => s.setSelection);
  const currentSimulationId = useSimulationStore((state) => state.currentSimulationId);
  const scenarios = useScenarios();
  const activeScenario = useActiveScenario();
  const getScenarioResults = useScenarioStore((state) => state.getScenarioResults);
  const getBaselineScenario = useScenarioStore((state) => state.getBaselineScenario);
  const runScenario = useScenarioStore((state) => state.runScenario);
  const setActiveScenario = useScenarioStore((state) => state.setActiveScenario);
  const createScenario = useScenarioStore((state) => state.createScenario);
  const deleteScenario = useScenarioStore((state) => state.deleteScenario);
  const updateScenario = useScenarioStore((state) => state.updateScenario);
  const getNextScenarioName = useScenarioStore((state) => state.getNextScenarioName);
  const { hasStaleScenarios, staleScenarios, getValidationErrors } = useScenarioValidation();
  
  // Get results for active scenario
  const activeScenarioResult = useMemo(() => {
    if (!activeScenario) return undefined;
    return getScenarioResults(activeScenario.id);
  }, [activeScenario, getScenarioResults]);

  const baselineScenario = getBaselineScenario();
  const baselineScenarioResult = useMemo(() => {
    // In the Baseline tab, compare against itself so deltas stay at zero.
    if (activeScenario?.isBaseline) {
      return activeScenarioResult;
    }
    if (!baselineScenario) return undefined;
    return getScenarioResults(baselineScenario.id);
  }, [activeScenario?.isBaseline, activeScenarioResult, baselineScenario, getScenarioResults]);

  // Use a proper selector that extracts the boolean value directly
  const isActiveScenarioRunning = useScenarioStore((state) => 
    activeScenario ? (state.isRunning.get(activeScenario.id) ?? false) : false
  );
  
  // Get running state and progress for all scenarios
  const scenarioRunningState = useScenarioStore((state) => state.isRunning);
  const scenarioProgressMap = useScenarioStore((state) => state.scenarioProgress);

  // Auto-run scenario if it doesn't have results
  useEffect(() => {
    if (!activeScenario || !graph.nodes.length) return;
    
    const hasResults = !!activeScenarioResult;
    
    // Run if scenario doesn't have results and isn't already running
    if (!hasResults && !isActiveScenarioRunning) {
      runScenario(activeScenario.id, graph, runConfig, runSimulation);
    }
  }, [activeScenario, activeScenarioResult, graph, runConfig, runScenario, runSimulation, isActiveScenarioRunning]);

  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const lastInitializedSimulationIdRef = useRef<string | null>(null);
  const saveSelectedNodeIdsRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Load shared selectedNodeIds once per simulation.
  // If scenarios disagree, prefer active scenario selection, then first scenario with a selection.
  useEffect(() => {
    if (!currentSimulationId) {
      lastInitializedSimulationIdRef.current = null;
      setSelectedNodeIds([]);
      return;
    }

    if (lastInitializedSimulationIdRef.current === currentSimulationId) {
      return;
    }

    const candidateSelection =
      activeScenario?.selectedNodeIds ??
      scenarios.find((scenario) => (scenario.selectedNodeIds?.length ?? 0) > 0)?.selectedNodeIds ??
      [];
    const availableIds = new Set(graph.nodes.map((node) => node.id));
    const validIds = candidateSelection.filter((id) => availableIds.has(id));
    setSelectedNodeIds(validIds);
    lastInitializedSimulationIdRef.current = currentSimulationId;
  }, [activeScenario?.selectedNodeIds, currentSimulationId, graph.nodes, scenarios]);

  // Save shared selectedNodeIds to every scenario (debounced)
  useEffect(() => {
    if (scenarios.length === 0) {
      return;
    }

    const scenariosNeedingUpdate = scenarios.filter((scenario) => {
      const scenarioNodeIds = scenario.selectedNodeIds ?? [];
      return !(
        scenarioNodeIds.length === selectedNodeIds.length &&
        scenarioNodeIds.every((id, idx) => id === selectedNodeIds[idx])
      );
    });

    if (scenariosNeedingUpdate.length === 0) {
      return;
    }

    if (saveSelectedNodeIdsRef.current) {
      clearTimeout(saveSelectedNodeIdsRef.current);
    }

    saveSelectedNodeIdsRef.current = setTimeout(async () => {
      try {
        await Promise.all(
          scenariosNeedingUpdate.map((scenario) =>
            updateScenario(scenario.id, {
              selectedNodeIds: selectedNodeIds.length > 0 ? selectedNodeIds : undefined,
            }),
          ),
        );
      } catch (error) {
        errorService.logError('Error saving shared selected nodes', error, {
          action: 'updateSharedSelectedNodeIds',
          component: 'ScenariosPanel',
          metadata: {
            scenarioIds: scenariosNeedingUpdate.map((scenario) => scenario.id),
          },
        });
      }
    }, 500); // 500ms debounce
    
    return () => {
      if (saveSelectedNodeIdsRef.current) {
        clearTimeout(saveSelectedNodeIdsRef.current);
      }
    };
  }, [scenarios, selectedNodeIds, updateScenario]);
  const [inputsPanelWidth, setInputsPanelWidth] = useState(280);
  const [isCreating, setIsCreating] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [newScenarioDescription, setNewScenarioDescription] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchQuery] = useState('');
  
  // Simulation name editing
  const currentSimulationName = useSimulationStore((state) => state.currentSimulationName);
  const currentVersionId = useSimulationStore((state) => state.currentVersionId);
  const currentAccessLevel = useSimulationStore((state) => state.currentAccessLevel);
  const setWorkspaceContext = useSimulationStore((state) => state.setWorkspaceContext);
  const updateSimulation = useVersionStore((state) => state.updateSimulation);
  const getVersion = useVersionStore((state) => state.getVersion);
  const isExample = useVersionStore((state) => state.isExample);
  const [isEditingSimulationName, setIsEditingSimulationName] = useState(false);
  const [localSimulationName, setLocalSimulationName] = useState('');
  const simulationNameInputRef = useRef<HTMLInputElement>(null);
  
  const isExampleSimulation = useMemo(() => {
    return currentSimulationId ? isExample(currentSimulationId) : false;
  }, [currentSimulationId, isExample]);
  const canManageScenarios = currentAccessLevel === 'owner' && !isExampleSimulation;
  
  // Sync local simulation name with store
  useEffect(() => {
    if (!isEditingSimulationName) {
      setLocalSimulationName(currentSimulationName || '');
    }
  }, [currentSimulationName, isEditingSimulationName]);
  
  // Focus input when editing starts
  useEffect(() => {
    if (!isEditingSimulationName) {
      return;
    }
    
    const frame = requestAnimationFrame(() => {
      simulationNameInputRef.current?.focus();
      simulationNameInputRef.current?.select();
    });
    
    return () => cancelAnimationFrame(frame);
  }, [isEditingSimulationName]);
  
  // Simulation name editing handlers
  const handleSimulationNameClick = useCallback(() => {
    if (isExampleSimulation || !currentSimulationId) {
      return;
    }
    setIsEditingSimulationName(true);
  }, [isExampleSimulation, currentSimulationId]);
  
  const cancelSimulationNameEdit = useCallback(() => {
    setLocalSimulationName(currentSimulationName || '');
    setIsEditingSimulationName(false);
  }, [currentSimulationName]);
  
  const commitSimulationName = useCallback(async () => {
    if (!currentSimulationId) {
      setIsEditingSimulationName(false);
      return;
    }
    
    const nextName = localSimulationName.trim();
    if (nextName.length === 0) {
      setLocalSimulationName(currentSimulationName || '');
      setIsEditingSimulationName(false);
      return;
    }
    
    if (nextName === currentSimulationName) {
      setIsEditingSimulationName(false);
      return;
    }
    
    try {
      await updateSimulation(currentSimulationId, { name: nextName });
      // Update local workspace context (preserve access level)
      setWorkspaceContext(
        currentSimulationId,
        nextName,
        currentVersionId,
        currentVersionId ? getVersion(currentVersionId)?.name : undefined,
        currentAccessLevel
      );
    } catch (error) {
      errorService.logError('Error updating simulation name', error, {
        action: 'updateSimulationName',
        component: 'ScenariosPanel'
      });
      // Revert on error
      setLocalSimulationName(currentSimulationName || '');
    } finally {
      setIsEditingSimulationName(false);
    }
  }, [currentSimulationId, currentSimulationName, currentVersionId, localSimulationName, updateSimulation, setWorkspaceContext, getVersion, currentAccessLevel]);
  
  const handleSimulationNameKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void commitSimulationName();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        cancelSimulationNameEdit();
      }
    },
    [commitSimulationName, cancelSimulationNameEdit]
  );

  // Keep selection in sync with available nodes
  useEffect(() => {
    setSelectedNodeIds((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const availableIds = new Set(graph.nodes.map((node) => node.id));
      const next = prev.filter((id) => availableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [graph.nodes]);

  // Filter nodes by selection and search query
  const filteredNodes = useMemo(() => {
    if (selectedNodeIds.length === 0) {
      return [];
    }
    const selectedSet = new Set(selectedNodeIds);
    let nodes = graph.nodes.filter((node) => selectedSet.has(node.id));
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      nodes = nodes.filter((node) => {
        const nameMatch = node.data.name.toLowerCase().includes(query);
        const tagsMatch = node.data.tags?.some((tag) => tag.toLowerCase().includes(query));
        const unitMatch = getNodeUnit(node, graph.nodes)?.toLowerCase().includes(query);
        return nameMatch || tagsMatch || unitMatch;
      });
    }
    
    return nodes;
  }, [graph.nodes, selectedNodeIds, searchQuery]);

  const handleNodeClick = useCallback(
    (nodeId: string) => {
      setSelection(nodeId);
    },
    [setSelection]
  );

  const handleNodeDeselect = useCallback(
    (nodeId: string) => {
      setSelectedNodeIds((prev) => prev.filter((id) => id !== nodeId));
    },
    []
  );

  const handleScenarioDeleteRequest = useCallback(async (scenario: Scenario) => {
    const confirmed = await confirmationService.confirm(
      `Are you sure you want to delete the scenario "${scenario.name}"? This action cannot be undone.`,
      {
        confirmText: 'Delete',
        cancelText: 'Cancel',
        isDestructive: true,
      }
    );

    if (confirmed) {
      await deleteScenario(scenario.id);
    }
  }, [deleteScenario]);

  // Handle creating new scenario
  const handleCreateScenario = useCallback(async () => {
    if (!currentSimulationId) {
      console.error('Cannot create scenario: no simulation ID available');
      return;
    }

    if (!canManageScenarios) {
      return;
    }
    
    if (!newScenarioName.trim()) {
      return;
    }
    
    setIsCreating(true);
    try {
      const created = await createScenario({
        simulationId: currentSimulationId,
        name: newScenarioName.trim(),
        description: newScenarioDescription.trim() || undefined,
        distributionOverrides: {},
        selectedNodeIds: selectedNodeIds.length > 0 ? selectedNodeIds : undefined,
        graphDataUpdatedAt: graph.dataUpdatedAt,
        isBaseline: false,
      }, false);
      
      if (created) {
        setNewScenarioName('');
        setNewScenarioDescription('');
        setShowCreateDialog(false);
      }
    } finally {
      setIsCreating(false);
    }
  }, [canManageScenarios, currentSimulationId, graph.dataUpdatedAt, createScenario, newScenarioName, newScenarioDescription, selectedNodeIds]);

  // Get validation errors for active scenario
  const activeScenarioErrors = activeScenario ? getValidationErrors(activeScenario.id) : [];

  // Resize handler for inputs panel
  const inputsPanelResize = useResize({
    initialWidth: inputsPanelWidth,
    minWidth: 280,
    maxWidth: 600,
    invertDirection: false, // Normal direction for left sidebar
    onResize: (newWidth) => {
      setInputsPanelWidth(newWidth);
    },
  });

  // Group nodes by unit for table sections
  const tableUnits = useMemo(() => {
    const unitMap = new Map<string | undefined, SimulationNode[]>();
    filteredNodes.forEach((node) => {
      const unit = getNodeUnit(node, graph.nodes);
      if (!unitMap.has(unit)) {
        unitMap.set(unit, []);
      }
      unitMap.get(unit)!.push(node);
    });

    // Sort units: undefined first, then alphabetically
    return Array.from(unitMap.keys()).sort((a, b) => {
      if (a === undefined && b === undefined) return 0;
      if (a === undefined) return -1;
      if (b === undefined) return 1;
      return a.localeCompare(b);
    });
  }, [filteredNodes, graph.nodes]);

  return (
    <div className="scenarios-panel">
      {/* Create scenario dialog */}
      {showCreateDialog && (
        <div className="scenario-create-dialog-overlay" onClick={() => setShowCreateDialog(false)}>
          <div className="scenario-create-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Scenario</h3>
            <div className="scenario-create-form">
              <div className="scenario-create-field">
                <label>Name *</label>
                <input
                  type="text"
                  value={newScenarioName}
                  onChange={(e) => setNewScenarioName(e.target.value)}
                  placeholder="Scenario name"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newScenarioName.trim()) {
                      handleCreateScenario();
                    } else if (e.key === 'Escape') {
                      setShowCreateDialog(false);
                    }
                  }}
                />
              </div>
              <div className="scenario-create-field">
                <label>Description</label>
                <textarea
                  value={newScenarioDescription}
                  onChange={(e) => setNewScenarioDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>
              <div className="scenario-create-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleCreateScenario}
                  disabled={!newScenarioName.trim() || isCreating}
                >
                  Create
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateDialog(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation banners */}
      {hasStaleScenarios && (
        <div className="scenario-stale-banner">
          <div className="scenario-stale-banner-content">
            <span className="scenario-stale-banner-icon">⚠️</span>
            <div className="scenario-stale-banner-text">
              <strong>{staleScenarios.length} scenario{staleScenarios.length > 1 ? 's' : ''} may be outdated</strong>
              <p>The graph has changed since these scenarios were created</p>
            </div>
          </div>
          <div className="scenario-stale-banner-actions">
            <button type="button" className="btn-secondary">
              Review
            </button>
          </div>
        </div>
      )}

      {activeScenarioErrors.length > 0 && (
        <div className="scenario-validation-banner">
          <div className="scenario-validation-banner-content">
            <span className="scenario-validation-banner-icon">❌</span>
            <div className="scenario-validation-banner-text">
              <strong>Scenario has validation errors</strong>
              <ul>
                {activeScenarioErrors.map((error, idx) => (
                  <li key={idx}>
                    {error.nodeName || error.nodeId}: {error.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Scenario header */}
      <div className="scenarios-panel-body">
        <aside
          className="sidebar scenarios-inputs-sidebar"
          style={{ width: `${inputsPanelWidth}px` }}
        >
          {/* Simulation name header */}
          {currentSimulationName && (
            <div className="sidebar-header">
              {isEditingSimulationName ? (
                <input
                  ref={simulationNameInputRef}
                  className="sidebar-simulation-name-input"
                  value={localSimulationName}
                  onChange={(event) => setLocalSimulationName(event.target.value)}
                  onBlur={() => { void commitSimulationName(); }}
                  onKeyDown={handleSimulationNameKeyDown}
                  onClick={(event) => event.stopPropagation()}
                />
              ) : (
                <h2 
                  className="sidebar-simulation-name" 
                  title={currentSimulationName}
                  onClick={handleSimulationNameClick}
                  style={{ cursor: isExampleSimulation ? 'default' : 'pointer' }}
                >
                  {currentSimulationName}
                </h2>
              )}
            </div>
          )}
          {/* Filter section above Input Distributions */}
          <div className="scenarios-sidebar-filter">
            <NodeCompareSelector
              selectedNodeIds={selectedNodeIds}
              onSelectionChange={setSelectedNodeIds}
            />
          </div>
          <ScenarioInputDistributionsPanel filteredNodes={filteredNodes} />
          <div 
            className="resize-handle resize-handle-right"
            onMouseDown={inputsPanelResize.handleMouseDown}
          />
        </aside>
        <div
          className="scenarios-panel-column"
          style={{ marginLeft: `${inputsPanelWidth}px` }}
        >
          {/* Scenario tabs at the top */}
          <div className="scenario-tabs">
            <div className="scenario-tabs-scroll">
              {scenarios.map((scenario) => (
                <ScenarioTab
                  key={scenario.id}
                  scenario={scenario}
                  isActive={activeScenario?.id === scenario.id}
                  isRunning={scenarioRunningState.get(scenario.id) ?? false}
                  progress={scenarioProgressMap.get(scenario.id)}
                  onSelect={() => setActiveScenario(scenario.id)}
                  onUpdate={canManageScenarios && !scenario.isBaseline ? updateScenario : undefined}
                  onDelete={canManageScenarios && !scenario.isBaseline ? handleScenarioDeleteRequest : undefined}
                />
              ))}
              {canManageScenarios && (
                <button
                  type="button"
                  className="scenario-tab-add"
                  onClick={() => {
                    setNewScenarioName(getNextScenarioName());
                    setNewScenarioDescription('');
                    setShowCreateDialog(true);
                  }}
                  disabled={isCreating}
                >
                  +
                </button>
              )}
            </div>
          </div>

          {/* Main content: table sections */}
          <div className="scenarios-panel-content">
            {isActiveScenarioRunning && !activeScenarioResult ? (
              <div className="scenarios-calculating-spinner">
                <span className="scenario-run-indicator-dot" />
              </div>
            ) : filteredNodes.length === 0 ? (
              <div className="scenarios-empty">
                <p>
                  {graph.nodes.length === 0
                    ? 'No nodes in the simulation.'
                    : searchQuery
                    ? 'No nodes match your search.'
                    : 'No nodes selected yet. Use the search on the left to choose which nodes to compare.'}
                </p>
              </div>
            ) : (
              <div className="scenarios-views-container">
                {tableUnits.map((unit, index) => {
                  const unitNodes = filteredNodes.filter((node) => getNodeUnit(node, graph.nodes) === unit);
                  return (
                    <div key={unit || 'no-unit'}>
                      <div className="scenarios-unit-pair">
                        <div className="scenarios-view-section scenarios-table-section">
                          <div className="scenarios-table-unit-group">
                            <ScenariosTable
                              nodes={unitNodes}
                              allNodes={graph.nodes}
                              result={activeScenarioResult}
                              baselineResult={baselineScenarioResult}
                              showComparison={!activeScenario?.isBaseline}
                              onNodeClick={handleNodeClick}
                              onNodeDeselect={handleNodeDeselect}
                              unit={unit}
                              title={unit || 'No Unit'}
                            />
                          </div>
                        </div>
                      </div>
                      {index < tableUnits.length - 1 && (
                        <div className="scenarios-unit-divider" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
